/**
 * Subscription flow management hook
 * Handles DocuSign integration and subscription signing
 *
 * Uses shared useDocumentState for document state management
 */

import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useSubcriptionDocuSign } from "../../../hooks/useDocuSign";
import usePostSubscriptionAgreementSigned from "../../../hooks/usePostSubscriptionAgreementSigned";
import useSignedDocument from "../../../hooks/useSignedDocument";
import { REST_SERVER, WEB_DOMAIN } from "../../../constants/domain";
import {
  MESSAGES,
  useDocumentState,
} from "../../../components/DocumentSigning";
import { clearSubscriptionSession } from "../utils/sessionStorage";
import {
  InvestmentOpportunity,
  SubscriptionInitResponse,
  SubscriptionResponse,
} from "../StructureNote.types";

/**
 * Hook to manage the subscription agreement signing flow
 * Uses shared useDocumentState for document state management
 *
 * @param token - Authentication token
 * @param regtankLivenessFeatureFlag - Feature flag for liveness verification
 * @returns Subscription state and flow functions
 */
export const useSubscriptionFlow = (
  token: string,
  regtankLivenessFeatureFlag?: boolean
) => {
  // Use shared document state hook
  const documentState = useDocumentState();

  // Subscription-specific state
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [offerSigned, setOfferSigned] = useState(false);

  const docuSignMutation = useSubcriptionDocuSign();
  const subscriptionAgreementMutation = usePostSubscriptionAgreementSigned();
  const signedDocumentMutation = useSignedDocument();

  /**
   * Initiate document signing flow
   */
  const initiateSubscription = useCallback(
    async (
      selectedData: InvestmentOpportunity,
      investorSubscribeAmount: string
    ): Promise<SubscriptionInitResponse | void> => {
      if (!selectedData) {
        toast.error("No investment opportunity selected.");
        return;
      }

      toast.info(MESSAGES.LOADING_DOC);
      setIsOverlayVisible(true);

      return new Promise<SubscriptionInitResponse>((resolve, reject) => {
        docuSignMutation.mutate(
          {
            token,
            structuredNoteId: selectedData.id,
            investedAmount: investorSubscribeAmount,
            webDomain: WEB_DOMAIN,
            redirectUrl: `${REST_SERVER}/document-management/docusign/webhook`,
            regtankLivenessFeatureFlag: regtankLivenessFeatureFlag || undefined,
          },
          {
            onSuccess: (data) => {
              documentState.updateDocument(
                data.url,
                data.documentId,
                data.completedAt
              );
              resolve(data);
            },
            onError: (e) => {
              toast.error((e as Error).message);
              setIsOverlayVisible(false);
              reject(e);
            },
          }
        );
      });
    },
    [token, docuSignMutation, regtankLivenessFeatureFlag, documentState]
  );

  /**
   * Complete subscription after signing
   * Returns subscription response data for caller to handle
   * Original logic from handleSubscriptionAgreementSigned (lines 230-270)
   */
  const completeSubscription = useCallback(
    async (
      selectedData: InvestmentOpportunity,
      investorSubscribeAmount: string
    ): Promise<SubscriptionResponse> => {
      if (!selectedData) {
        toast.error("No investment opportunity selected.");
        throw new Error("No investment opportunity selected.");
      }

      return new Promise<SubscriptionResponse>((resolve, reject) => {
        subscriptionAgreementMutation.mutate(
          {
            token,
            structuredNoteId: selectedData.id,
            investorSubscribedAmount: investorSubscribeAmount,
          },
          {
            onSuccess: (data) => {
              // Only show success toast for full subscriptions
              // Let caller handle partial subscription UI
              if (!data.isPartialSubscription) {
                toast.success(MESSAGES.SIGNING_SUCCESS);
              }
              // Clear session storage (original lines 245-251)
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              setOfferSigned(true);
              resolve(data);
            },
            onError: (e) => {
              // Clear session storage (original lines 257-263)
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              reject(e);
            },
          }
        );
      });
    },
    [token, subscriptionAgreementMutation]
  );

  /**
   * Fetch signed document after signing
   */
  const fetchSignedDocument = useCallback(() => {
    documentState.clearUrl();
    signedDocumentMutation.mutate(
      { documentId: documentState.documentId, token },
      {
        onSuccess: (data) => {
          documentState.updateDocument(
            data.url,
            data.documentId,
            data.completedAt
          );
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  }, [documentState, token, signedDocumentMutation]);

  return {
    // Document state from shared hook
    realUrl: documentState.realUrl,
    documentId: documentState.documentId,
    completedAt: documentState.completedAt,
    viewerKey: documentState.viewerKey,

    // Setters for session restore compatibility
    setRealUrl: (url: string) =>
      documentState.updateDocument(
        url,
        documentState.documentId,
        documentState.completedAt
      ),
    setDocumentId: (id: number) =>
      documentState.updateDocument(
        documentState.realUrl,
        id,
        documentState.completedAt
      ),
    setCompletedAt: (completed: string) =>
      documentState.updateDocument(
        documentState.realUrl,
        documentState.documentId,
        completed
      ),

    // Subscription-specific state
    isOverlayVisible,
    offerSigned,

    // Actions
    initiateSubscription,
    completeSubscription,
    fetchSignedDocument,
    incrementViewerKey: documentState.incrementViewerKey,

    // Loading state
    isLoading:
      docuSignMutation.isLoading || subscriptionAgreementMutation.isLoading,
  };
};
